import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import Icon from './Icon';
import { C, FONT, SHADOW, RAIL_WIDTH, RAIL_WIDTH_COLLAPSED } from './tokens';

import Logo from 'images/logo-app.png';
import LogoIcon from 'images/logo-icon.png';

// A single leaf (deepest nav item). Renders as an anchor so Rails/Turbolinks
// handles the full-page navigation.
const Leaf = ({ leaf, active, height }) => (
  <Box
    component="a"
    href={leaf.url}
    target={leaf.target || '_self'}
    sx={{
      display: 'flex',
      alignItems: 'center',
      height,
      px: '12px',
      borderRadius: '8px',
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      font: `${active ? 700 : 400} 12px/1 ${FONT}`,
      color: active ? C.railLeafActive : C.railLeaf,
      bgcolor: active ? C.railLeafActiveBg : 'transparent',
      '&:hover': { bgcolor: C.railLeafActiveBg, color: C.railLeafActive },
      '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: -2 },
    }}
  >
    {leaf.name}
  </Box>
);

// Group row: a direct link (no leaves) or an accordion/flyout toggle.
const GroupRow = ({ group, expanded, isActive, isOpen, height, onClick, onFlyout }) => {
  const hasLeaves = group.leaves && group.leaves.length > 0;
  const isLink = !hasLeaves;

  const row = (
    <Box
      component={isLink ? 'a' : 'div'}
      href={isLink ? group.url : undefined}
      role={isLink ? undefined : 'button'}
      aria-expanded={hasLeaves ? isOpen : undefined}
      aria-haspopup={hasLeaves && !expanded ? 'menu' : undefined}
      tabIndex={0}
      onClick={(e) => (expanded ? onClick(e) : onFlyout(e))}
      onKeyDown={(e) => {
        if (!isLink && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          expanded ? onClick(e) : onFlyout(e);
        }
      }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        height,
        px: expanded ? '12px' : 0,
        ...(expanded ? {} : { width: 44, mx: 'auto', justifyContent: 'center' }),
        borderRadius: '8px',
        cursor: 'pointer',
        userSelect: 'none',
        textDecoration: 'none',
        color: isActive ? '#fff' : C.railText,
        bgcolor: isActive ? C.railActiveBg : 'transparent',
        '&:hover': { bgcolor: isActive ? C.railActiveBg : 'rgba(255,255,255,.05)' },
        '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: -2 },
      }}
    >
      {isActive && (
        <Box sx={{
          position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
          borderRadius: '0 3px 3px 0', bgcolor: C.turquoise,
        }} />
      )}
      <Icon name={group.icon} size={18} color={isActive ? C.turquoise : C.railIcon} />
      {expanded && (
        <Box component="span" sx={{
          font: `${isActive ? 700 : 400} 12.5px/1 ${FONT}`,
          flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {group.name}
        </Box>
      )}
      {expanded && group.chip && (
        <Box component="span" sx={{
          font: `600 9px/1 ${FONT}`, color: C.railIcon,
          border: '1px solid rgba(255,255,255,.18)', px: '6px', py: '4px', borderRadius: '999px',
        }}>
          {group.chip}
        </Box>
      )}
      {expanded && hasLeaves && (
        <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={15}
          color={isOpen ? C.railIcon : C.railSubheader} />
      )}
    </Box>
  );

  // Collapsed rail: every row gets a tooltip with the group name (the label is hidden).
  return expanded ? row : <Tooltip title={group.name} placement="right" arrow>{row}</Tooltip>;
};

const Rail = ({
  sections, expanded, touch, active, openGroupId, onToggleGroup, onToggleRail,
  currentUser, logoutLink, rootURL,
}) => {
  const [flyout, setFlyout] = useState(null); // { anchorEl, group }
  const [userMenuEl, setUserMenuEl] = useState(null);

  // Larger hit targets on touch/mobile (accessibility requirement in the handoff).
  const rowHeight = touch ? 44 : 36;
  const leafHeight = touch ? 38 : 30;

  const openFlyout = (group) => (e) => {
    if (!group.leaves || group.leaves.length === 0) return; // direct link handled by href
    e.preventDefault();
    setFlyout((f) => (f && f.group.id === group.id ? null : { anchorEl: e.currentTarget, group }));
  };
  const closeFlyout = () => setFlyout(null);

  return (
    <Box sx={{
      width: expanded ? RAIL_WIDTH : RAIL_WIDTH_COLLAPSED,
      height: '100%',
      bgcolor: C.ink,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Logo header */}
      <Box
        component="a"
        href={rootURL}
        sx={{
          height: 58, flex: 'none', display: 'flex', alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'center',
          borderBottom: `1px solid ${C.railBorder}`, px: '18px', textDecoration: 'none',
        }}
      >
        {expanded ? (
          <Box component="img" src={Logo} alt="Unimart Hub" sx={{ height: 26, width: 'auto', display: 'block' }} />
        ) : (
          <Box component="img" src={LogoIcon} alt="Unimart Hub"
            sx={{ width: 30, height: 30, borderRadius: '8px', display: 'block', objectFit: 'cover' }} />
        )}
      </Box>

      {/* Scrollable list */}
      {/* A classic scrollbar (mouse attached, or "always show") painted a white track that narrowed the list
          against the logo and the footer: thin and on the rail's own ink instead */}
      <Box sx={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden', p: '12px 10px',
        display: 'flex', flexDirection: 'column', gap: '10px',
        scrollbarWidth: 'thin', scrollbarColor: `${C.railSubheader} transparent`,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: C.railSubheader, borderRadius: 3 },
      }}>
        {sections.map((section, si) => (
          <Box key={si} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {expanded && section.label && (
              <Box component="div" sx={{
                font: `700 10px/1 ${FONT}`, letterSpacing: '.13em', color: C.railSubheader,
                p: '0 12px 7px', whiteSpace: 'nowrap', textTransform: 'uppercase',
              }}>
                {section.label}
              </Box>
            )}
            {!expanded && section.label && (
              <Box sx={{ height: '1px', bgcolor: C.railBorder, m: '5px 12px' }} />
            )}

            {section.groups.map((group) => {
              const hasLeaves = group.leaves && group.leaves.length > 0;
              const isActive = group.id === active.groupId;
              const isOpen = expanded && openGroupId === group.id;
              return (
                <Box key={group.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <GroupRow
                    group={group}
                    expanded={expanded}
                    isActive={isActive}
                    isOpen={isOpen}
                    height={rowHeight}
                    onClick={() => hasLeaves && onToggleGroup(group.id)}
                    onFlyout={openFlyout(group)}
                  />
                  {expanded && hasLeaves && (
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ display: 'flex', flexDirection: 'column', p: '3px 0 4px 25px' }}>
                        {group.leaves.map((leaf) => (
                          <Leaf key={leaf.name} leaf={leaf} height={leafHeight}
                            active={isActive && leaf.name === active.leafName} />
                        ))}
                      </Box>
                    </Collapse>
                  )}
                  {/* Wiki divider */}
                  {expanded && group.dividerAfter && (
                    <Box sx={{ height: '1px', bgcolor: C.railBorder, m: '7px 10px 2px' }} />
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Footer: user + collapse toggle */}
      <Box sx={{
        flex: 'none', borderTop: `1px solid ${C.railBorder}`, p: '11px',
        display: 'flex', alignItems: 'center', gap: '10px',
        justifyContent: expanded ? 'flex-start' : 'center',
      }}>
        <Box
          component="button"
          onClick={(e) => setUserMenuEl(e.currentTarget)}
          aria-haspopup="menu"
          aria-label={`Cuenta de ${currentUser.name}`}
          sx={{
            display: 'flex', alignItems: 'center', gap: '10px', flex: expanded ? 1 : 'none',
            minWidth: 0, background: 'none', border: 0, p: 0, cursor: 'pointer', textAlign: 'left',
            '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: 2, borderRadius: '8px' },
          }}
        >
          <Box sx={{
            width: 29, height: 29, flex: 'none', borderRadius: '999px', bgcolor: C.turquoise,
            color: '#fff', display: 'grid', placeItems: 'center', font: `700 11px/1 ${FONT}`,
          }}>
            {currentUser.initials}
          </Box>
          {expanded && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
              <Box component="span" sx={{
                font: `700 11.5px/1 ${FONT}`, color: '#fff', whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{currentUser.name}</Box>
              <Box component="span" sx={{ font: `400 10.5px/1 ${FONT}`, color: '#8a919e' }}>
                {currentUser.role}
              </Box>
            </Box>
          )}
        </Box>
        {expanded && (
          <Tooltip title="Colapsar el rail" placement="top" arrow>
            <Box
              component="button"
              onClick={onToggleRail}
              aria-label="Colapsar el rail"
              sx={{
                width: 28, height: 28, flex: 'none', borderRadius: '7px', display: 'grid',
                placeItems: 'center', color: '#8a919e', cursor: 'pointer', background: 'none', border: 0,
                '&:hover': { bgcolor: 'rgba(255,255,255,.09)', color: '#fff' },
                '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: 2 },
              }}
            >
              <Icon name="panel-left-close" size={17} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Flyout for collapsed rail */}
      <Popover
        open={Boolean(flyout)}
        anchorEl={flyout ? flyout.anchorEl : null}
        onClose={closeFlyout}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={8}
        PaperProps={{
          onMouseLeave: closeFlyout,
          sx: {
            width: 252, ml: '8px', bgcolor: '#fff', border: `1px solid ${C.border}`,
            borderRadius: '10px', boxShadow: SHADOW.popover, p: '7px',
            display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {flyout && (
          <>
            <Box sx={{
              font: `700 10px/1 ${FONT}`, letterSpacing: '.12em', color: '#8a919e',
              p: '9px 11px 7px', textTransform: 'uppercase',
            }}>{flyout.group.name}</Box>
            {flyout.group.leaves.map((leaf) => {
              const activeLeaf = flyout.group.id === active.groupId && leaf.name === active.leafName;
              return (
                <Box
                  key={leaf.name}
                  component="a"
                  href={leaf.url}
                  target={leaf.target || '_self'}
                  onClick={closeFlyout}
                  sx={{
                    p: '9px 11px', borderRadius: '7px', cursor: 'pointer', whiteSpace: 'nowrap',
                    textDecoration: 'none',
                    font: `${activeLeaf ? 700 : 400} 12px/1 ${FONT}`,
                    color: activeLeaf ? C.yellowText : C.textMid,
                    bgcolor: activeLeaf ? C.yellowTint : 'transparent',
                    '&:hover': { bgcolor: activeLeaf ? C.yellowTint : C.borderSoft },
                  }}
                >
                  {leaf.name}
                </Box>
              );
            })}
          </>
        )}
      </Popover>

      {/* User menu (logout) */}
      <Menu
        anchorEl={userMenuEl}
        open={Boolean(userMenuEl)}
        onClose={() => setUserMenuEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem
          component="a"
          href={logoutLink.url}
          data-method={logoutLink.method || 'delete'}
          onClick={() => setUserMenuEl(null)}
          sx={{ font: `600 12.5px/1.4 ${FONT}`, color: C.error }}
        >
          {logoutLink.name}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Rail;
